import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthProvider';
import { useTheme } from '../contexts/ThemeProvider';
import logoWhite from '../assets/logo-repro-white.svg';
import logoDark from '../assets/logo-repro-v3-dark.svg';

export default function MfaSetup() {
  const { authState, setupMfa, confirmMfa } = useAuth();
  const { resolvedTheme } = useTheme();

  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'loading' | 'scan' | 'verify'>('loading');

  useEffect(() => {
    if (authState !== 'needs_mfa_setup') return;

    const initSetup = async () => {
      try {
        const data = await setupMfa();
        setQrCodeUrl(data.qrCodeDataUrl);
        setSecret(data.secret);
        setStep('scan');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to set up MFA');
        setStep('scan');
      }
    };

    initSetup();
  }, [authState, setupMfa]);

  if (authState === 'authenticated') return <Navigate to="/dashboard" replace />;
  if (authState !== 'needs_mfa_setup') return <Navigate to="/login" replace />;

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await confirmMfa(totpCode);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const logo = resolvedTheme === 'dark' ? logoWhite : logoDark;

  return (
    <div className="min-h-screen flex items-center justify-center bg-base-100 px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img src={logo} alt="RePro" className="h-8 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-base-content">Set Up Two-Factor Auth</h1>
          <p className="text-sm text-base-content/60 mt-1">
            MFA is required for all admin accounts
          </p>
        </div>

        <div className="card bg-base-200 shadow-md">
          <div className="card-body gap-4">
            {step === 'loading' && (
              <div className="text-center py-8">
                <span className="loading loading-spinner loading-md" />
              </div>
            )}

            {step === 'scan' && (
              <div className="space-y-4">
                {error && <div className="alert alert-error text-sm">{error}</div>}

                <div className="text-sm text-base-content/70 space-y-2">
                  <p>
                    <strong>Step 1:</strong> Scan this QR code with your authenticator app (Google
                    Authenticator, Authy, etc.).
                  </p>
                </div>

                {qrCodeUrl && (
                  <div className="flex justify-center py-2">
                    <img
                      src={qrCodeUrl}
                      alt="MFA QR Code"
                      className="w-48 h-48 rounded-lg bg-white p-2"
                    />
                  </div>
                )}

                {secret && (
                  <div className="text-center">
                    <p className="text-xs text-base-content/50 mb-1">
                      Or enter this code manually:
                    </p>
                    <code className="text-xs bg-base-300 px-2 py-1 rounded font-mono select-all break-all">
                      {secret}
                    </code>
                  </div>
                )}

                <button onClick={() => setStep('verify')} className="btn btn-primary w-full">
                  I've scanned the code
                </button>
              </div>
            )}

            {step === 'verify' && (
              <form onSubmit={handleVerify} className="space-y-4">
                {error && <div className="alert alert-error text-sm">{error}</div>}

                <div className="text-sm text-base-content/70">
                  <p>
                    <strong>Step 2:</strong> Enter the 6-digit code from your authenticator app to
                    verify.
                  </p>
                </div>

                <div className="form-control">
                  <label className="label" htmlFor="totpCode">
                    <span className="label-text">Verification Code</span>
                  </label>
                  <input
                    id="totpCode"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    required
                    autoFocus
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                    className="input input-bordered w-full text-center tracking-widest font-mono"
                    placeholder="000000"
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setStep('scan');
                      setError('');
                      setTotpCode('');
                    }}
                    className="btn btn-outline flex-1"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={loading || totpCode.length !== 6}
                    className="btn btn-primary flex-1"
                  >
                    {loading ? (
                      <span className="loading loading-spinner loading-sm" />
                    ) : (
                      'Verify & Enable'
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
